import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Eye, EyeOff, X } from "lucide-react";
import { toast } from "sonner";
import { admin, errText, fmtDate } from "../../lib/adminApi";
import { SERVICE_CATEGORIES } from "../../data/site";

const blank = { title: "", domain: SERVICE_CATEGORIES[1].title, location: "", experience: "", employment_type: "Full-time", description: "", skills: "", is_active: true };
const inp = "w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-brand-orange [&>option]:bg-[#0A1F44]";
const TYPES = ["Full-time", "Part-time", "Internship", "Contract", "Remote"];

export const OpeningsPanel = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await admin.get("/careers/admin/openings");
      setItems(data.items);
    } catch (e) {
      toast.error(errText(e?.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (o) => setEditing(o ? { ...o, skills: o.skills.join(", ") } : { ...blank });
  const setF = (k) => (e) => setEditing((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...editing, skills: editing.skills.split(",").map((s) => s.trim()).filter(Boolean) };
    delete body.id; delete body.created_at; delete body.updated_at;
    try {
      if (editing.id) await admin.put(`/careers/admin/openings/${editing.id}`, body);
      else await admin.post("/careers/admin/openings", body);
      toast.success(editing.id ? "Opening updated" : "Opening published");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(errText(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (o) => {
    try {
      await admin.put(`/careers/admin/openings/${o.id}`, { ...o, is_active: !o.is_active });
      load();
    } catch (err) {
      toast.error(errText(err?.response?.data?.detail));
    }
  };

  const remove = async (o) => {
    if (!window.confirm(`Delete opening "${o.title}"?`)) return;
    try {
      await admin.delete(`/careers/admin/openings/${o.id}`);
      toast.success("Opening deleted");
      load();
    } catch (err) {
      toast.error(errText(err?.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-openings">
      <div className="flex items-center justify-between gap-4 mb-6">
        <p className="text-slate-400 text-sm">Only <span className="text-white">active</span> openings appear publicly on the Careers page. Publish verified vacancies only.</p>
        <button onClick={() => startEdit(null)} data-testid="admin-opening-new" className="btn-primary !py-2.5 !px-5 text-sm shrink-0"><Plus size={16} /> New opening</button>
      </div>

      {items.length === 0 ? (
        <div data-testid="admin-openings-empty" className="glass rounded-2xl p-10 text-center text-slate-500 text-sm">No openings yet. The Careers page currently shows the general profile submission message.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((o) => (
            <div key={o.id} data-testid={`admin-opening-card-${o.id}`} className="glass rounded-2xl p-6 flex flex-col">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <span className="font-mono text-[10px] tracking-wider uppercase text-brand-orange">{o.domain}</span>
                  <h3 className="font-head text-lg text-white font-semibold mt-1">{o.title}</h3>
                  <p className="text-xs text-slate-400 mt-1">{o.location} · {o.experience} · {o.employment_type}</p>
                </div>
                <span data-testid={`admin-opening-state-${o.id}`} className={`px-2.5 py-1 rounded-full text-[11px] font-medium ${o.is_active ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-500/20 text-slate-300"}`}>{o.is_active ? "Active" : "Hidden"}</span>
              </div>
              <p className="text-slate-400 text-sm mt-3 line-clamp-3">{o.description}</p>
              <div className="flex flex-wrap gap-1.5 mt-3">{o.skills.map((s) => <span key={s} className="px-2 py-0.5 rounded-full text-[11px] bg-white/5 border border-white/10 text-slate-300">{s}</span>)}</div>
              <div className="mt-auto pt-4 flex items-center gap-3 text-sm">
                <button onClick={() => startEdit(o)} data-testid={`admin-opening-edit-${o.id}`} className="inline-flex items-center gap-1 text-slate-300 hover:text-white"><Pencil size={14} /> Edit</button>
                <button onClick={() => toggle(o)} data-testid={`admin-opening-toggle-${o.id}`} className="inline-flex items-center gap-1 text-slate-300 hover:text-white">{o.is_active ? <><EyeOff size={14} /> Hide</> : <><Eye size={14} /> Publish</>}</button>
                <button onClick={() => remove(o)} data-testid={`admin-opening-delete-${o.id}`} className="ml-auto inline-flex items-center gap-1 text-red-400 hover:text-red-300"><Trash2 size={14} /> Delete</button>
                <span className="text-[11px] text-slate-600 hidden sm:inline">{fmtDate(o.updated_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setEditing(null)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} data-testid="admin-opening-form" className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-navy-deep border border-white/10 rounded-3xl p-6 md:p-8 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-head text-xl text-white font-semibold">{editing.id ? "Edit opening" : "New opening"}</h3>
              <button type="button" onClick={() => setEditing(null)} aria-label="Close" className="text-slate-400 hover:text-white"><X size={20} /></button>
            </div>
            <input data-testid="opening-title" required placeholder="Job title *" value={editing.title} onChange={setF("title")} className={inp} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <select data-testid="opening-domain" value={editing.domain} onChange={setF("domain")} className={inp}>
                {SERVICE_CATEGORIES.map((c) => <option key={c.id} value={c.title}>{c.title}</option>)}
                <option value="UI/UX Design">UI/UX Design</option>
              </select>
              <select data-testid="opening-type" value={editing.employment_type} onChange={setF("employment_type")} className={inp}>{TYPES.map((t) => <option key={t}>{t}</option>)}</select>
              <input data-testid="opening-location" required placeholder="Location * (e.g. Bulandshahr, UP / Remote)" value={editing.location} onChange={setF("location")} className={inp} />
              <input data-testid="opening-experience" required placeholder="Experience * (e.g. 2–4 years, Fresher)" value={editing.experience} onChange={setF("experience")} className={inp} />
            </div>
            <textarea data-testid="opening-description" required rows={4} minLength={10} placeholder="Short description * (what the role does, min 10 characters)" value={editing.description} onChange={setF("description")} className={`${inp} resize-none`} />
            <input data-testid="opening-skills" placeholder="Required skills, comma separated (max 15)" value={editing.skills} onChange={setF("skills")} className={inp} />
            <label className="flex items-center gap-3 text-sm text-slate-300"><input data-testid="opening-active" type="checkbox" checked={editing.is_active} onChange={setF("is_active")} className="h-4 w-4 accent-[#F26522]" /> Active — visible on the public Careers page</label>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setEditing(null)} className="btn-secondary !py-2.5 !px-5 text-sm">Cancel</button>
              <button type="submit" disabled={saving} data-testid="opening-save" className="btn-primary !py-2.5 !px-5 text-sm disabled:opacity-60">{saving ? "Saving…" : editing.id ? "Save changes" : "Publish opening"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default OpeningsPanel;
